import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';

import useMenuStore from 'stores/menu';
import useGameStore, { PURCHASE_VIEW } from 'stores/game';
import useGameStatsStore from 'stores/current-game';
import { useChatStore } from 'stores/chat';
import usePurchaseStore from 'stores/purchase';
import bingoAssetsStore from 'stores/bingoAssetsStore';
import useBreakpoint, { MOBILE, TABLET_LANDSCAPE } from 'hooks/useBreakpoint';

import GameInfoBar from './GameInfoBar/GameInfoBar';
import UserBingoCards from './UserBingoCards/UserBingoCards';
import PurchaseBingoCards from './PurchaseBingoCards/PurchaseBingoCards';

import styles from './BingoPanel.module.scss';

const menuExpandedSelector = (state) => state.isMenuExpanded;
const menuOpenSelector = (state) => state.isMenuOpen;
const gameStateSelector = (state) => state.gameState;
const chatSelector = (state) => state.isChatExpanded;
const currentGameSelector = (state) => state.currentGame;
const bingoCardsListSelector = (state) => [
  state.bingoCardsList,
  state.setBingoCardsList,
];
const fetchGameAssetsSelector = (state) => state.fetchGameAssets;
const getGameAssetsSelector = (state) => state.getGameAssets;

function BingoPanel() {
  const isMenuExpanded = useMenuStore(menuExpandedSelector);
  const isMenuOpen = useMenuStore(menuOpenSelector);
  const isChatExpanded = useChatStore(chatSelector);
  const gameState = useGameStore(gameStateSelector);
  const currentGame = useGameStatsStore(currentGameSelector);

  const isPurchase = gameState === PURCHASE_VIEW;
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === MOBILE;
  const isOpenSidemenu = isChatExpanded || isMenuOpen;

  const [currentGameID, setGameID] = useState(null);
  const [bingoCardsList, setBingoCardsList] = usePurchaseStore(
    bingoCardsListSelector,
    shallow
  );

  const fetchGameAssets = bingoAssetsStore(fetchGameAssetsSelector);
  const getGameAssets = bingoAssetsStore(getGameAssetsSelector);

  useEffect(() => {
    if (currentGame && currentGame.bingoRoundId !== currentGameID) {
      setGameID(currentGame.bingoRoundId);
      if (bingoCardsList.length) setBingoCardsList([]);

      fetchGameAssets(currentGame);
    }
  }, [
    currentGame,
    currentGameID,
    fetchGameAssets,
    bingoCardsList.length,
    setBingoCardsList,
  ]);

  const assets = getGameAssets(currentGame);

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.isMenuExpanded]: isMenuExpanded || isPurchase,
        [styles.isLandscape]: breakpoint === TABLET_LANDSCAPE,
      })}
      style={{
        backgroundImage: assets.backgroundBig
          ? `url(${assets.backgroundBig})`
          : 'none',
      }}
    >
      <div
        className={clsx(styles.infoBar, {
          [styles.isHidden]: isMobile && isOpenSidemenu && !isPurchase,
          [styles.isLandscape]: breakpoint === TABLET_LANDSCAPE,
        })}
      >
        <GameInfoBar />
      </div>
      {assets.logoBig ? (
        <img
          src={assets.logoBig}
          alt=""
          className={clsx(styles.logo, {
            [styles.isHidden]: isMobile && isOpenSidemenu && !isPurchase,
            [styles.isLandscape]: breakpoint === TABLET_LANDSCAPE,
          })}
        />
      ) : (
        <div className={clsx(styles.logo, styles.minHeight)} />
      )}

      <div
        className={clsx(styles.cards, {
          [styles.isMenuExpanded]: isMenuExpanded || isPurchase,
          [styles.isSideMenuOpen]: isOpenSidemenu && !isPurchase,
          [styles.isLandscape]: breakpoint === TABLET_LANDSCAPE,
        })}
      >
        {isPurchase ? (
          <PurchaseBingoCards isSideMenuOpen={isMobile && isOpenSidemenu} />
        ) : (
          <UserBingoCards isSideMenuOpen={isMobile && isOpenSidemenu} />
        )}
      </div>
    </div>
  );
}

export default BingoPanel;
